import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { CustomRangeDatePicker } from './CustomRangeDatePicker';
import { HorizontalPills } from './HorizontalPills';
import { Routes } from 'routes';
import { createStyles } from 'utils/createStyle';

export type DateRangeSelectorProps = {
    onChange: (start: Date | undefined, end: Date | undefined) => void;
    hideOrderHistoryReportCopy?: boolean;
};

const formatDisplayDate = (date: Date) => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Intl.DateTimeFormat('en-US', options).format(date);
};

const styles = createStyles({
    container: 'text-xs',
    item: 'inline-block border border-brand-dark rounded-full ml-1',
    itemSelected: 'bg-pale-blue font-bold',
    text: 'px-2 inline-block text-brand',
});

const useOrderHistoryDateRangeSelectorControls = (onChange: DateRangeSelectorProps['onChange']) => {
    const [selectedValue, setSelectedValue] = useState('');
    const [startDate, setStartDate] = useState<Date>();
    const [endDate, setEndDate] = useState<Date>();

    const onDateRangeChange = useCallback(
        (s: Date | undefined, e: Date | undefined) => {
            setStartDate(s);
            setEndDate(e);
            onChange(s, e);
        },
        [setStartDate, setEndDate, onChange]
    );

    const [openDatePicker, setOpenDatePicker] = useState(false);

    const onCustomClicked = useCallback(() => {
        setOpenDatePicker(true);
    }, []);

    const items = [
        { text: '3 months', value: '3' },
        { text: '6 months', value: '6' },
        { text: '12 months', value: '12' },
        { text: <span onClick={onCustomClicked}>Custom...</span>, value: 'custom' },
    ];

    const selectedRange = useMemo(() => {
        if (startDate && endDate) {
            return `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`;
        }
        return '';
    }, [startDate, endDate]);

    const onItemChange = useCallback(
        (item: string) => {
            setSelectedValue(item);

            if (item === 'custom') {
                onChange(startDate, endDate);
            } else {
                const months = parseInt(item, 10);

                const newStartDate = new Date();
                const newEndDate = new Date();

                newStartDate.setMonth(newStartDate.getMonth() - months);

                setStartDate(newStartDate);
                setEndDate(newEndDate);

                onChange(newStartDate, newEndDate);
            }
        },
        [onChange, startDate, endDate]
    );

    useEffect(() => {
        onItemChange('3');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        selectedRange,
        items,
        selectedValue,
        onItemChange,
        openDatePicker,
        onDateRangeChange,
        setOpenDatePicker,
    };
};

export const OrderHistoryDateRangeSelector = ({ onChange }: DateRangeSelectorProps) => {
    const {
        selectedRange,
        items,
        selectedValue,
        onItemChange,
        openDatePicker,
        onDateRangeChange,
        setOpenDatePicker,
    } = useOrderHistoryDateRangeSelectorControls(onChange);
    return (
        <div>
            <div className="block md:flex">
                <div className="text-base sm:text-base text-brand-dark truncate flex items-center">
                    {selectedRange}
                </div>
                <div className="flex flex-col items-end ml-auto truncate my-4">
                    <HorizontalPills
                        style={styles as any}
                        items={items}
                        value={selectedValue}
                        onChange={onItemChange}
                    />
                    <p className="whitespace-normal text-grey-1 text-xs mt-2">
                        <a
                            target="_blank"
                            href={Routes.FamilyOrderHistory}
                            className="cursor-pointer"
                        >
                            Order History report
                        </a>{' '}
                        for orders older than 18 months.
                    </p>
                </div>
            </div>
            <div className="block text-right">
                <div className="inline-block">
                    {selectedValue === 'custom' && (
                        <CustomRangeDatePicker
                            open={openDatePicker}
                            onChange={onDateRangeChange}
                            onClose={() => setOpenDatePicker(false)}
                            applyFilterBtn="Filter Orders by Date"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export const NewOrderHistoryDateRangeSelector = ({
    onChange,
    hideOrderHistoryReportCopy = false,
}: DateRangeSelectorProps) => {
    const {
        selectedRange,
        items,
        selectedValue,
        onItemChange,
        openDatePicker,
        onDateRangeChange,
        setOpenDatePicker,
    } = useOrderHistoryDateRangeSelectorControls(onChange);

    return (
        <div>
            <div className="block md:flex md:flex-col lg:items-end md:ml-auto">
                <div className="text-base sm:text-base text-brand-dark truncate">
                    {selectedRange}
                </div>
                <div className="flex flex-col md:items-end ml-auto truncate my-4">
                    <HorizontalPills
                        style={styles as any}
                        items={items}
                        value={selectedValue}
                        onChange={onItemChange}
                    />

                    {!hideOrderHistoryReportCopy && (
                        <p className="whitespace-normal text-grey-1 text-xs mt-2">
                            <a
                                target="_blank"
                                href={Routes.FamilyOrderHistory}
                                className="cursor-pointer"
                            >
                                Order History report
                            </a>{' '}
                            for orders older than 18 months.
                        </p>
                    )}
                </div>
            </div>
            <div className="block text-right">
                <div className="inline-block">
                    {selectedValue === 'custom' && (
                        <CustomRangeDatePicker
                            open={openDatePicker}
                            onChange={onDateRangeChange}
                            onClose={() => setOpenDatePicker(false)}
                            applyFilterBtn="Filter Orders by Date"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
